import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useState } from 'react';
import {
  Labeled,
  useGetIdentity,
  useRecordContext,
  useRefresh,
  useUpdate,
} from 'react-admin';

import { FieldWithAction } from './FieldWithAction';

type EditDialogProps = {
  open: boolean;
  onClose: () => void;
  label: string;
  value: number | null;
  onSave: (value: number | null) => Promise<void>;
  isLoading: boolean;
  helperText?: string;
  inputProps?: Record<string, unknown>;
};

const EditDialog = ({
  open,
  onClose,
  label,
  value,
  onSave,
  isLoading,
  helperText,
  inputProps,
}: EditDialogProps) => {
  const [localValue, setLocalValue] = useState<string>(
    value === null ? '' : value.toString(),
  );

  const handleSave = useCallback(async () => {
    const numValue =
      localValue === '' || localValue === null
        ? null
        : Number(localValue);
    if (!isNaN(numValue as number) || numValue === null) {
      await onSave(numValue);
      onClose();
    }
  }, [localValue, onSave, onClose]);

  const handleClose = useCallback(() => {
    setLocalValue(value === null ? '' : value.toString());
    onClose();
  }, [value, onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          label={label}
          type="number"
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          helperText={helperText}
          inputProps={inputProps}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export type NumericOverrideFieldProps = {
  field: string;
  label: string;
  helperText?: string;
  inputProps?: Record<string, unknown>;
  emptyText?: string;
};

export const NumericOverrideField = ({
  field,
  label,
  helperText,
  inputProps,
  emptyText = 'Not set',
}: NumericOverrideFieldProps) => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const { data: identity } = useGetIdentity();
  const [updateTherapist, { isLoading: isSaving }] = useUpdate(
    'therapists',
    { id: record?.id },
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleSave = useCallback(
    async (value: number | null) => {
      if (!record) return;
      await updateTherapist(
        'therapists',
        {
          id: record.id,
          data: {
            [field]: value,
            lastUpdatedAt: new Date(),
            lastUpdatedBy: identity?.id,
          },
        },
        { onSuccess: () => refresh() },
      );
    },
    [record, updateTherapist, field, identity?.id, refresh],
  );

  if (!record) return null;

  const currentValue =
    (record as Record<string, number | null | undefined>)[field] ?? null;

  return (
    <Box>
      <Labeled label={label}>
        <FieldWithAction
          value={currentValue !== null ? currentValue.toString() : null}
          emptyText={emptyText}
          maxWidth="240px"
          showActionOnHover={false}
          action={
            <Tooltip title={helperText ?? ''}>
              <IconButton
                size="small"
                onClick={() => setDialogOpen(true)}
                disabled={isSaving}
                color="primary"
                sx={{ padding: '4px' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
      </Labeled>
      <EditDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        label={label}
        value={currentValue}
        onSave={handleSave}
        isLoading={isSaving}
        helperText={helperText}
        inputProps={inputProps}
      />
    </Box>
  );
};
